import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import pdfHeaderFooter from '@/services/utils/pdf/pdfHeaderFooter';
import figureFormatter from '@/services/utils/figureFormatter';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

const productionOrderTrackerPdf = () => {
    const { getHeader, getFooter } = pdfHeaderFooter();
    const { commaFormat } = figureFormatter();

    const exportToPDF = (company, data, tabTitle, userName, qrcode, totals = {}) => {
        var doc = {
            pageSize: 'A4',
            pageOrientation: 'landscape',
            pageMargins: [20, 100, 20, 80],
            header: getHeader(company),
            footer: getFooter(userName, qrcode),
            content: getContent(data, tabTitle, totals),
            styles: {
                header: {
                    fontSize: 18
                }
            },
            defaultStyle: {
                color: 'black',
                fontSize: 8,
                font: 'SulaimanLipi'
            },
            info: {
                title: 'Production Order Tracker - ' + tabTitle
            }
        }

        pdfMake.createPdf(doc).open();
    }

    const getStatCard = (label, value) => {
        return {
            table: {
                widths: ['100%'],
                heights: [20, 16],
                body: [
                    [{ text: label, bold: true, fontSize: 8, color: 'black', alignment: 'center', fillColor: '#f2f2f2', margin: [4, 3, 4, 0] }],
                    [{ text: String(value), bold: true, fontSize: 11, color: 'black', alignment: 'center', fillColor: '#f2f2f2', margin: [4, 0, 4, 3] }]
                ]
            },
            layout: {
                hLineWidth: function (i, node) {
                    return (i === 0 || i === node.table.body.length) ? 0.5 : 0;
                },
                vLineWidth: function (i, node) {
                    return (i === 0 || i === node.table.widths.length) ? 0.5 : 0;
                },
                hLineColor: function () { return 'black'; },
                vLineColor: function () { return 'black'; }
            }
        };
    }

    const getStatsSection = (totals) => {
        return {
            stack: [
                {
                    columns: [
                        getStatCard('Order Qty', totals.total_pi_quantity || 0),
                        getStatCard('Production Qty', totals.total_production_quantity || 0),
                        getStatCard('Delivered Qty', totals.total_delivered_quantity || 0),
                        getStatCard('Pending Delivery Qty', totals.pending_delivery_quantity || 0),
                        getStatCard('Ready for Delivery Qty', totals.ready_for_delivery_quantity || 0)
                    ],
                    columnGap: 8,
                    margin: [0, 0, 0, 8]
                },
                {
                    columns: [
                        getStatCard('Pending Production Qty', totals.pending_production_quantity || 0),
                        getStatCard('Order Value', commaFormat(totals.total_amount)),
                        getStatCard('Delivery Value', commaFormat(totals.total_delivery_amount)),
                        getStatCard('Paid Amount', commaFormat(totals.paid_amount)),
                        getStatCard('Due Amount', commaFormat(totals.due_amount))
                    ],
                    columnGap: 8
                }
            ],
            margin: [0, 0, 0, 12]
        };
    }

    const getContent = (data, tabTitle, totals) => {
        return [
            {
                text: 'Production Order Tracker - ' + tabTitle,
                style: 'header',
                alignment: 'center',
                margin: [0, 0, 20, 10],
                bold: true,
                color: 'black',
                decoration: 'underline'
            },
            getStatsSection(totals),
            {
                style: 'tableExample',
                margin: [0, 10, 0, 0],
                table: {
                    widths: ['auto', 'auto', '*', '*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
                    body: getTableBody(data)
                },
                layout: {
                    fillColor: function (rowIndex) {
                        return rowIndex === 0 ? '#f3f2f7' : null;
                    },
                    hLineWidth: function () { return 0.5; },
                    vLineWidth: function () { return 0.5; }
                }
            }
        ]
    }

    const getTableBody = (data) => {
        return [
            getTableHead(),
            ...getTableRows(data)
        ]
    }

    const getTableHead = () => {
        return [
            { text: 'BILL NUMBER' },
            { text: 'BILL DATE' },
            { text: 'CUSTOMER' },
            { text: 'BUYER' },
            { text: 'CURRENCY' },
            { text: 'ORDER QTY', alignment: 'right' },
            { text: 'PRODUCTION QTY', alignment: 'right' },
            { text: 'DELIVERED QTY', alignment: 'right' },
            { text: 'PENDING DELIVERY QTY', alignment: 'right' },
            { text: 'READY FOR DELIVERY QTY', alignment: 'right' },
            { text: 'PENDING PRODUCTION QTY', alignment: 'right' },
            { text: 'ORDER VALUE', alignment: 'right' },
            { text: 'DELIVERY VALUE', alignment: 'right' },
            { text: 'DUE AMOUNT', alignment: 'right' }
        ]
    }

    const getTableRows = (data) => {
        return data.map((item) => [
            { text: item.bill_number },
            { text: item.date },
            { text: item.party_name },
            { text: item.buyer_name },
            { text: item.currency },
            { text: commaFormat(item.total_qty), alignment: 'right' },
            { text: commaFormat(item.total_production_qty), alignment: 'right' },
            { text: commaFormat(item.delivered_qty), alignment: 'right' },
            { text: commaFormat(item.total_qty - item.delivered_qty), alignment: 'right' },
            { text: commaFormat(item.total_production_qty - item.delivered_qty), alignment: 'right' },
            { text: commaFormat(item.total_qty - item.total_production_qty), alignment: 'right' },
            { text: commaFormat(item.bill_amount), alignment: 'right' },
            { text: commaFormat(item.delivered_value), alignment: 'right' },
            { text: commaFormat(item.due_amount), alignment: 'right' }
        ])
    }

    return {
        exportToPDF
    }
}

export default productionOrderTrackerPdf;
